import Card from './Card';
import { Spinner } from './Spinner';

export const getWorkspaceStatusClass = (status) => {
  switch (status) {
    case 'active':
    case 'approved':
    case 'completed':
    case 'covered':
    case 'healthy':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
    case 'pending':
    case 'request_submitted':
    case 'payment_pending':
    case 'payment_review':
    case 'attention':
    case 'urgent':
    case 'pending_signature':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
    case 'draft':
    case 'planning':
    case 'in_progress':
      return 'bg-[#fce7f3] text-[#b91c66] ring-1 ring-[#f4b6d2]';
    case 'inactive':
    case 'blocked':
    case 'unassigned':
      return 'bg-stone-100 text-stone-700 ring-1 ring-stone-200';
    case 'declined':
    case 'failed':
      return 'bg-red-50 text-red-700 ring-1 ring-red-200';
    case 'issued':
    case 'signed':
    case 'verified':
    case 'verified_signed':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
    default:
      return 'bg-stone-100 text-stone-700 ring-1 ring-stone-200';
  }
};

export const WorkspaceStatusChip = ({ status, label }) => (
  <span
    className={`inline-flex min-h-[26px] items-center rounded-full px-2.5 text-[11px] font-semibold ${getWorkspaceStatusClass(
      status,
    )}`}
  >
    {label || status || 'not started'}
  </span>
);

export const WorkspacePageLoader = () => (
  <div className="mx-auto flex min-h-[50vh] w-full max-w-[min(100%,96rem)] items-center justify-center py-12 sm:py-16">
    <Spinner size="lg" />
  </div>
);

export const WorkspacePageError = ({ children, className = '' }) => (
  <div
    className={`mx-auto w-full max-w-[min(100%,96rem)] py-8 text-center text-sm text-red-600 ${className}`}
  >
    {children}
  </div>
);

export const WorkspaceShell = ({
  title,
  description,
  actions,
  sidebar,
  children,
  className = '',
}) => (
  <div
    className={`mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-0.5 sm:space-y-4 ${className}`}
  >
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
          Workspace
        </p>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-stone-950 sm:text-[1.85rem] xl:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="max-w-3xl text-sm leading-5 text-stone-600">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-3 [&_a]:w-full [&_button]:w-full sm:[&_a]:w-auto sm:[&_button]:w-auto">
          {actions}
        </div>
      ) : null}
    </div>

    {sidebar ? (
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] 2xl:grid-cols-[300px_minmax(0,1fr)]">
        <div className="min-w-0 space-y-3 sm:space-y-4">{sidebar}</div>
        <div className="min-w-0 space-y-3 sm:space-y-4">{children}</div>
      </div>
    ) : (
      <div className="min-w-0 space-y-3 sm:space-y-4">{children}</div>
    )}
  </div>
);

export const WorkspaceRail = ({ title, description, children, action }) => (
  <Card title={title} action={action} className="h-fit">
    {description ? (
      <p className="mb-3 text-sm leading-5 text-stone-600">{description}</p>
    ) : null}
    <div className="space-y-3">{children}</div>
  </Card>
);

export const WorkspaceSection = ({
  title,
  description,
  status,
  statusLabel,
  action,
  children,
  className = '',
}) => (
  <Card
    title={
      <div className="space-y-1">
        <div className="text-lg font-semibold tracking-tight text-stone-950">
          {title}
        </div>
        {description ? (
          <p className="text-sm font-normal leading-5 text-stone-600">{description}</p>
        ) : null}
      </div>
    }
    action={
      <div className="flex flex-wrap items-center gap-3">
        {status ? (
          <WorkspaceStatusChip status={status} label={statusLabel} />
        ) : null}
        {action}
      </div>
    }
    className={className}
  >
    {children}
  </Card>
);

export const WorkspaceStats = ({
  children,
  columns = 'xl:grid-cols-2 2xl:grid-cols-4',
  stacked = false,
}) => (
  <div
    className={
      stacked
        ? 'grid grid-cols-1 gap-3'
        : `grid grid-cols-1 gap-3 sm:grid-cols-2 ${columns}`
    }
  >
    {children}
  </div>
);

export const WorkspaceStat = ({ label, value, hint, status }) => (
  <div className="rounded-[20px] border border-stone-200 bg-white px-4 py-3.5 shadow-[0_12px_32px_-28px_rgba(28,25,23,0.35)]">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-stone-500">
          {label}
        </p>
        <p className="mt-2 break-words text-xl font-semibold tracking-tight text-stone-950 sm:text-2xl">
          {value}
        </p>
      </div>
      {status ? <WorkspaceStatusChip status={status} /> : null}
    </div>
    {hint ? <p className="mt-2 text-sm leading-5 text-stone-600">{hint}</p> : null}
  </div>
);

export const WorkspaceEmptyState = ({
  title,
  message,
  action,
  className = '',
}) => (
  <div
    className={`rounded-[24px] border border-dashed border-stone-300 bg-stone-50 px-5 py-10 text-center ${className}`}
  >
    <p className="text-lg font-semibold text-stone-900">{title}</p>
    {message ? <p className="mt-2 text-sm leading-6 text-stone-600">{message}</p> : null}
    {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
  </div>
);

export const WorkspaceSelectableList = ({
  items,
  selectedId,
  onSelect,
  getKey,
  renderItem,
  emptyTitle,
  emptyMessage,
}) => {
  if (!items.length) {
    return (
      <WorkspaceEmptyState
        title={emptyTitle}
        message={emptyMessage}
        className="py-6"
      />
    );
  }

  return (
    <div className="space-y-2.5">
      {items.map((item) => {
        const key = getKey(item);
        const selected = selectedId === key;

        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={`w-full rounded-[18px] border px-3.5 py-3 text-left transition ${
              selected
                ? 'border-[#f4b6d2] bg-[#fdf2f8] shadow-[0_12px_32px_-26px_rgba(157,23,77,0.28)]'
                : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50'
            }`}
          >
            {renderItem(item, selected)}
          </button>
        );
      })}
    </div>
  );
};
